/**
 * Email channel — per-page components for the domain-app shell.
 *
 * Unlike WhatsApp's self-contained tabs, the Email tab components are prop-driven,
 * so each page instantiates the useEmailCommunications hook and wires the relevant
 * tab + modal (mirroring the logic previously colocated in Email/index.tsx).
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconLayoutDashboard,
  IconSend,
  IconTemplate,
  IconPencil,
  IconUsersGroup,
  IconSettings,
  IconSpeakerphone,
} from '@tabler/icons-react';
import { Group, Loader } from '../../../../components/ui';
import { ChannelPage } from '../_shared/ChannelPage';
import { ChannelDashboard } from '../_shared/ChannelDashboard';
import { ProviderSettingsForm } from '../_shared/ProviderSettingsForm';
import { BulkSendWizard } from '../_shared/BulkSendWizard';
import { CampaignsList, CampaignDetail } from '../_shared/CampaignsView';
import { useEmailCommunications } from './hooks/useEmailCommunications';
import { normalizeEmailHtml } from '../_shared/emailHtml';
import {
  EmailQueueTab,
  EmailTemplatesTab,
  EmailTestTab,
  EmailQueueModal,
  EmailTemplateModal,
} from './components';
import type { EmailApiResponse, AppEmailTemplateModelView, EmailTestData } from './types';

export function EmailDashboardPage() {
  return (
    <ChannelPage
      icon={<IconLayoutDashboard size={28} />}
      title="Email"
      subtitle="Overview of the email channel. Use the left menu to compose, review the outbox, manage templates, and configure the channel."
    >
      <ChannelDashboard channel="email" />
    </ChannelPage>
  );
}

export function EmailComposePage() {
  const { loading, templates, fetchTemplates, createEmail } = useEmailCommunications();

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSend = async (data: EmailTestData) => {
    const emailData = {
      toEmail: data.recipient,
      fromEmail: 'noreply@actschurch.co.za',
      fromName: data.fromName?.trim() || 'CCMS Notifications',
      ccEmail: data.ccEmail || null,
      bccEmail: data.bccEmail || null,
      replyToEmail: data.replyTo?.trim() || null,
      subject: data.subject,
      body: data.isHTML ? normalizeEmailHtml(data.body) : data.body,
      attachmentsJson: data.attachments && data.attachments.length > 0 ? JSON.stringify(data.attachments) : null,
      payload: data.payload || null,
      date: new Date().toISOString(),
      isHTML: data.isHTML,
      priority: data.priority,
      scheduledDateTime: data.scheduledDateTime || null,
      templateID: data.templateId || null,
      status: 'Pending',
      isSent: false,
      returnReceipt: false,
      retryCount: 0,
      maxRetryCount: 3,
    };
    await createEmail(emailData as any);
  };

  return (
    <ChannelPage icon={<IconPencil size={28} />} title="Compose" subtitle="Compose and queue an email.">
      <EmailTestTab templates={templates} loading={loading} onSend={handleSend} />
    </ChannelPage>
  );
}

export function EmailBulkSendPage() {
  return (
    <ChannelPage icon={<IconUsersGroup size={28} />} title="Bulk Send" subtitle="Send to many recipients via a campaign.">
      <BulkSendWizard channel="email" />
    </ChannelPage>
  );
}

export function EmailCampaignsPage() {
  const navigate = useNavigate();
  return (
    <ChannelPage icon={<IconSpeakerphone size={28} />} title="Campaigns" subtitle="Bulk campaign history with delivery counts and per-recipient status.">
      <CampaignsList channel="email" onOpen={(id) => navigate(`/admin/communications/email/campaigns/${id}`)} />
    </ChannelPage>
  );
}

export function EmailCampaignDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  return (
    <ChannelPage
      icon={<IconSpeakerphone size={28} />}
      title="Campaigns"
      subtitle="Delivery counts and per-recipient status."
      onBack={() => navigate('/admin/communications/email/campaigns')}
    >
      <CampaignDetail campaignId={Number(id)} />
    </ChannelPage>
  );
}

export function EmailOutboxPage() {
  const {
    loading,
    queueItems,
    fetchQueue,
    retryFailedItem,
    updatePriority,
    updateQueueItem,
  } = useEmailCommunications();

  const [modalOpened, setModalOpened] = useState(false);
  const [selected, setSelected] = useState<EmailApiResponse | null>(null);
  const [mode, setMode] = useState<'view' | 'edit'>('view');

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const handleSave = async (data: Partial<EmailApiResponse>) => {
    if (selected) {
      await updateQueueItem(selected.emailID, data as any);
      setModalOpened(false);
      setSelected(null);
    }
  };

  return (
    <ChannelPage icon={<IconSend size={28} />} title="Outbox" subtitle="Queued & sent emails with delivery status.">
      {loading && queueItems.length === 0 ? (
        <Group justify="center" p="xl">
          <Loader size="lg" />
        </Group>
      ) : (
        <EmailQueueTab
          queueItems={queueItems}
          loading={loading}
          onRefresh={fetchQueue}
          onView={(item: EmailApiResponse) => { setSelected(item); setMode('view'); setModalOpened(true); }}
          onEdit={(item: EmailApiResponse) => { setSelected(item); setMode('edit'); setModalOpened(true); }}
          onRetry={retryFailedItem}
          onUpdatePriority={updatePriority}
        />
      )}
      <EmailQueueModal
        opened={modalOpened}
        onClose={() => { setModalOpened(false); setSelected(null); }}
        item={selected}
        mode={mode}
        onSave={handleSave}
        loading={loading}
      />
    </ChannelPage>
  );
}

export function EmailTemplatesPage() {
  const {
    loading,
    templates,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  } = useEmailCommunications();

  const [modalOpened, setModalOpened] = useState(false);
  const [selected, setSelected] = useState<AppEmailTemplateModelView | null>(null);
  const [mode, setMode] = useState<'view' | 'edit' | 'create'>('view');

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSave = async (data: any) => {
    if (mode === 'create') {
      await createTemplate(data);
    } else if (selected) {
      await updateTemplate(selected.templateID, data);
    }
    setModalOpened(false);
    setSelected(null);
  };

  return (
    <ChannelPage icon={<IconTemplate size={28} />} title="Templates" subtitle="Reusable email templates.">
      {loading && templates.length === 0 ? (
        <Group justify="center" p="xl">
          <Loader size="lg" />
        </Group>
      ) : (
        <EmailTemplatesTab
          templates={templates}
          loading={loading}
          onRefresh={fetchTemplates}
          onView={(t: AppEmailTemplateModelView) => { setSelected(t); setMode('view'); setModalOpened(true); }}
          onEdit={(t: AppEmailTemplateModelView) => { setSelected(t); setMode('edit'); setModalOpened(true); }}
          onDelete={deleteTemplate}
          onCreate={() => { setSelected(null); setMode('create'); setModalOpened(true); }}
        />
      )}
      <EmailTemplateModal
        opened={modalOpened}
        onClose={() => { setModalOpened(false); setSelected(null); }}
        template={selected}
        mode={mode}
        onSave={handleSave}
        loading={loading}
        onSwitchToEdit={() => setMode('edit')}
      />
    </ChannelPage>
  );
}

export function EmailSettingsPage() {
  return (
    <ChannelPage icon={<IconSettings size={28} />} title="Settings" subtitle="Provider selection (AWS SES) and channel configuration.">
      <ProviderSettingsForm channel="email" />
    </ChannelPage>
  );
}
